import React, { useCallback, useState, useEffect } from 'react';
import type { IntegrationDto, User } from '../../types';
import PullRunsCancelButton from '../PullRunsCancelButton';

interface IntegrationInfoModalProps {
    integration: IntegrationDto | null;
    isOpen: boolean;
    onClose: () => void;
    currentUser?: User | null;
}

interface LogModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    content: string;
    isRunning?: boolean;
    refreshInterval?: number;
    onRefreshLog?: () => void;
}

interface LogEntry {
    timestamp: string | null;
    message: string;
    severity: 'error' | 'warning' | 'info';
}

const logEntryTimestampPattern = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3})\s+(.*)$/;
const errorLogPattern = /\b(error|fatal|exception|failed|fail|ошибка)\b|исключени/i;
const warningLogPattern = /\b(warn|warning|предупреждени)\b/i;

const getLogSeverity = (message: string): LogEntry['severity'] => {
    if (errorLogPattern.test(message)) {
        return 'error';
    }

    if (warningLogPattern.test(message)) {
        return 'warning';
    }

    return 'info';
};

const parseLogEntries = (content: string): LogEntry[] => {
    const entries: LogEntry[] = [];

    for (const line of content.split(/\r?\n/)) {
        if (!line && entries.length === 0) {
            continue;
        }

        const match = logEntryTimestampPattern.exec(line);
        if (match) {
            entries.push({
                timestamp: match[1],
                message: match[2],
                severity: getLogSeverity(match[2])
            });
            continue;
        }

        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
            lastEntry.message = lastEntry.message
                ? `${lastEntry.message}\n${line}`
                : line;
            lastEntry.severity = getLogSeverity(lastEntry.message);
            continue;
        }

        entries.push({
            timestamp: null,
            message: line,
            severity: getLogSeverity(line)
        });
    }

    return entries.filter(entry => entry.timestamp !== null || entry.message.trim().length > 0);
};

const LogModal: React.FC<LogModalProps> = ({
    isOpen,
    onClose,
    title,
    content,
    isRunning = false,
    refreshInterval = 3,
    onRefreshLog
}) => {
    const logRef = React.useRef<HTMLDivElement>(null);
    const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(true);
    const logEntries = React.useMemo(() => parseLogEntries(content), [content]);

    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    // Auto-refresh log for running tasks
    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;

        if (isOpen && isRunning && refreshInterval > 0 && onRefreshLog) {
            intervalId = setInterval(() => {
                // Before refreshing, check if scrolled to bottom
                const logElement = logRef.current;
                if (logElement) {
                    const { scrollTop, scrollHeight, clientHeight } = logElement;
                    const atBottom = Math.abs(scrollTop + clientHeight - scrollHeight) < 5; // tolerance
                    setIsScrolledToBottom(atBottom);
                }

                onRefreshLog();
            }, refreshInterval * 1000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isOpen, isRunning, refreshInterval, onRefreshLog]);

    // Auto-scroll to bottom when content updates if user was at bottom
    useEffect(() => {
        const logElement = logRef.current;
        if (logElement && isScrolledToBottom) {
            // Small timeout to ensure DOM update is complete
            setTimeout(() => {
                logElement.scrollTop = logElement.scrollHeight;
            }, 0);
        }
    }, [content, isScrolledToBottom]);

    // Initial scroll to bottom when modal opens
    useEffect(() => {
        if (isOpen && logRef.current) {
            setTimeout(() => {
                if (logRef.current) {
                    logRef.current.scrollTop = logRef.current.scrollHeight;
                }
            }, 0);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleDownload = () => {
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="modal-backdrop-custom" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div className="d-flex align-items-center">
                            <div className="client-avatar mr-3">📄</div>
                            <div>
                                <h3 className="modal-title-custom">
                                    {title}
                                    {isRunning && (
                                        <span className="ml-2" style={{
                                            display: 'inline-block',
                                            width: '8px',
                                            height: '8px',
                                            backgroundColor: '#007bff',
                                            borderRadius: '50%',
                                            animation: 'pulse 2s infinite'
                                        }}></span>
                                    )}
                                </h3>
                                <p className="modal-subtitle-custom">
                                    Содержимое журнала
                                    {isRunning && ` (автообновление каждые ${refreshInterval} сек)`}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close-custom"
                            onClick={onClose}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body-custom">
                        <div className="log-viewer" ref={logRef}>
                            {logEntries.map((entry, index) => (
                                <div key={index} className={`log-entry log-entry-${entry.severity}`}>
                                    {entry.timestamp && (
                                        <span className="log-entry-timestamp">{entry.timestamp}</span>
                                    )}
                                    <span className="log-entry-message">{entry.message}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="modal-footer-custom">
                        <button
                            type="button"
                            className="btn-primary-custom"
                            onClick={handleDownload}
                        >
                            <span className="btn-icon">💾</span>
                            Сохранить
                        </button>
                        <button
                            type="button"
                            className="btn-secondary-custom"
                            onClick={onClose}
                        >
                            <span className="btn-icon">✕</span>
                            Закрыть
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const IntegrationInfoModal: React.FC<IntegrationInfoModalProps> = ({
    integration,
    isOpen,
    onClose,
}) => {
    const [pullRuns, setPullRuns] = useState<{
        id: number;
        integrationId: number;
        startTime: string;
        endTime: string | null;
        duration: number | null;
        success: boolean;
        errorMessage: string | null;
        output: string | null;
        errorOutput: string | null;
    }[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [, setLoading] = useState<boolean>(false);
    const [logModal, setLogModal] = useState<{
        isOpen: boolean;
        title: string;
        content: string;
        pullRunId?: number;
        type?: 'output' | 'error';
        isRunning?: boolean;
    }>({
        isOpen: false,
        title: '',
        content: ''
    });
    const [refreshInterval, setRefreshInterval] = useState<number>(() => {
        // Загрузить сохраненное значение из localStorage или использовать 3 по умолчанию
        const saved = localStorage.getItem('integrationRefreshInterval');
        return saved ? parseInt(saved, 10) : 3;
    });
    const [pageSize, setPageSize] = useState<number>(() => {
        // Загрузить сохраненное значение из localStorage или использовать 10 по умолчанию
        const saved = localStorage.getItem('integrationPageSize');
        return saved ? parseInt(saved, 10) : 10;
    });
    const [currentPage, setCurrentPage] = useState<number>(1);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Никогда';
        return new Date(dateString).toLocaleString('ru-RU');
    };

    const formatDuration = (seconds: number | null) => {
        if (!seconds) return '—';
        const roundedSeconds = Math.round(seconds * 1000) / 1000; // Округлить до 3 знаков после запятой
        if (roundedSeconds < 60) return `${roundedSeconds.toFixed(3)}с`;
        const minutes = Math.floor(roundedSeconds / 60);
        const remainingSeconds = roundedSeconds % 60;
        return `${minutes}м ${remainingSeconds.toFixed(3)}с`;
    };

    const loadPullRuns = useCallback(async (page = currentPage, size = pageSize, isRefresh = false) => {
        if (!integration) return;

        try {
            if (!isRefresh) {
                setLoading(true);
            }

            const params = new URLSearchParams({
                page: page.toString(),
                pageSize: size.toString()
            });

            const response = await fetch(`/api/pullruns/integration/${integration.id}?${params}`);

            if (response.ok) {
                const result = await response.json();
                setPullRuns(result.data.data);
                setTotalCount(result.data.totalCount);
                setCurrentPage(result.data.page);
            } else {
                console.error('Failed to load pull runs');
            }
        } catch (error) {
            console.error('Error loading pull runs:', error);
        } finally {
            if (!isRefresh) {
                setLoading(false);
            }
        }
    }, [currentPage, integration, pageSize]);

    useEffect(() => {
        if (isOpen && integration) {
            loadPullRuns();
        }
    }, [isOpen, integration, loadPullRuns]);

    // Auto-refresh effect
    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;

        if (refreshInterval > 0 && isOpen && integration) {
            intervalId = setInterval(() => {
                loadPullRuns(currentPage, pageSize, true);
            }, refreshInterval * 1000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [refreshInterval, isOpen, integration, currentPage, pageSize, loadPullRuns]);

    // Handle page change
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        loadPullRuns(page, pageSize);
    };

    // Handle page size change
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
        loadPullRuns(1, size);
    };

    // localStorage effect - сохраняем выбранное значение
    useEffect(() => {
        localStorage.setItem('integrationRefreshInterval', refreshInterval.toString());
    }, [refreshInterval]);

    useEffect(() => {
        localStorage.setItem('integrationPageSize', pageSize.toString());
        setCurrentPage(1); // Сброс на первую страницу при изменении pageSize
        loadPullRuns(1, pageSize);
    }, [loadPullRuns, pageSize]);

    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen || !integration) {
        return null;
    }

    const handleViewLog = async (type: 'output' | 'error', pullRunId: number) => {
        try {
            // Find the pull run to check if it's still running
            const pullRun = pullRuns.find(pr => pr.id === pullRunId);
            const isRunning = pullRun ? pullRun.endTime === null : false;

            const response = await fetch(`/api/pullruns/${pullRunId}/${type}`);

            if (response.ok) {
                const result = await response.json();
                const title = `${type === 'output' ? 'Output' : 'Error'} Log - Pull Run #${pullRunId}`;
                setLogModal({
                    isOpen: true,
                    title,
                    content: result.data.content || `No ${type} log available`,
                    pullRunId,
                    type,
                    isRunning
                });
            } else {
                console.error('Failed to load log');
                setLogModal({
                    isOpen: true,
                    title: `${type === 'output' ? 'Output' : 'Error'} Log - Pull Run #${pullRunId}`,
                    content: `Failed to load ${type} log. Please try again later.`,
                    pullRunId,
                    type,
                    isRunning
                });
            }
        } catch (error) {
            console.error('Error loading log:', error);
            setLogModal({
                isOpen: true,
                title: `${type === 'output' ? 'Output' : 'Error'} Log - Pull Run #${pullRunId}`,
                content: `Error loading ${type} log: ${error instanceof Error ? error.message : 'Unknown error'}`,
                pullRunId,
                type,
                isRunning: false
            });
        }
    };

    const refreshLog = async () => {
        if (!logModal.pullRunId || !logModal.type) return;

        try {
            const response = await fetch(`/api/pullruns/${logModal.pullRunId}/${logModal.type}`);

            if (response.ok) {
                const result = await response.json();
                setLogModal(prev => ({
                    ...prev,
                    content: result.data.content || `No ${logModal.type} log available`,
                    isRunning: prev.isRunning // Keep the running status
                }));
            }
            // For refresh, we don't show errors to avoid disrupting the modal
        } catch (error) {
            console.error('Error refreshing log:', error);
            // Keep the existing content on refresh failure
        }
    };

    const closeLogModal = () => {
        setLogModal({ isOpen: false, title: '', content: '' });
    };

    const handleCancelSuccess = () => {
        // Refresh the pull runs list when a cancellation is successful
        loadPullRuns(currentPage, pageSize, true);
    };

    const handleCancelError = (pullRunId: number, error: string) => {
        console.error(`Failed to cancel pull run ${pullRunId}:`, error);
        // Could show toast notification here if desired
    };

    const renderPagination = () => {
        const totalPages = Math.ceil(totalCount / pageSize);
        if (totalPages <= 1) return null;

        return (
            <div className="pagination-container" style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <button
                    className="btn btn-small btn-secondary-custom"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    style={{ margin: '0 0.25rem' }}
                    title="В начало"
                >
                    «
                </button>
                <button
                    className="btn btn-small btn-secondary-custom"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{ margin: '0 0.25rem' }}
                    title="Предыдущая"
                >
                    ‹
                </button>
                {(() => {
                    const pages = [];
                    const startPage = Math.max(1, currentPage - 2);
                    const endPage = Math.min(totalPages, currentPage + 2);

                    for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                            <button
                                key={i}
                                className={`btn btn-small ${i === currentPage ? 'btn-primary-custom' : 'btn-secondary-custom'}`}
                                onClick={() => handlePageChange(i)}
                                style={{ margin: '0 0.25rem' }}
                            >
                                {i}
                            </button>
                        );
                    }
                    return pages;
                })()}
                <button
                    className="btn btn-small btn-secondary-custom"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{ margin: '0 0.25rem' }}
                    title="Следующая"
                >
                    ›
                </button>
                <button
                    className="btn btn-small btn-secondary-custom"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    style={{ margin: '0 0.25rem' }}
                    title="В конец"
                >
                    »
                </button>
            </div>
        );
    };

    return (
        <div className="modal-backdrop-custom" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div className="d-flex align-items-center">
                            <div className="client-avatar mr-3">
                                {integration.name?.charAt(0).toUpperCase() || 'И'}
                            </div>
                            <div>
                                <h3 className="modal-title-custom">#{integration.id}: {integration.name} ({integration.supplierName})</h3>
                                <p className="modal-subtitle-custom">История синхронизаций</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close-custom"
                            onClick={onClose}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body-custom">
                        <div className="pull-runs-section">
                            {totalCount > pageSize && renderPagination()}
                            <div className="table-responsive">
                                <table className="table table-hover table-striped">
                                    <thead className="table-dark">
                                        <tr>
                                            <th style={{ textAlign: 'center' }}>Время запуска</th>
                                            <th>Продолжительность</th>
                                            <th>Статус</th>
                                            <th style={{ textAlign: 'center' }}>Действия</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pullRuns.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                                    Нет данных о синхронизациях
                                                </td>
                                            </tr>
                                        ) : (
                                            pullRuns.map(pullRun => (
                                                <React.Fragment key={pullRun.id}>
                                                    <tr>
                                                        <td style={{ textAlign: 'center' }}>{formatDate(pullRun.startTime)}</td>
                                                        <td style={{ textAlign: 'center' }}>{formatDuration(pullRun.duration)}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <span className={`status-badge ${!pullRun.endTime ? 'pending' : (pullRun.success ? 'success' : 'error')}`}>
                                                                {!pullRun.endTime ? 'В ПРОЦЕССЕ' : (pullRun.success ? 'Успешно' : 'Ошибка')}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                                                                <button
                                                                    className="btn btn-small btn-info"
                                                                    onClick={() => handleViewLog('output', pullRun.id)}
                                                                    title="Просмотреть log"
                                                                >
                                                                    📄
                                                                </button>
                                                                <PullRunsCancelButton
                                                                    pullRun={{
                                                                        ...pullRun,
                                                                        integrationName: integration.name || 'Unknown Integration',
                                                                        endTime: pullRun.endTime || undefined,
                                                                        duration: pullRun.duration || undefined,
                                                                        success: pullRun.success,
                                                                        errorMessage: pullRun.errorMessage || undefined,
                                                                        output: pullRun.output || undefined,
                                                                        errorOutput: pullRun.errorOutput || undefined
                                                                    }}
                                                                    onCancelSuccess={handleCancelSuccess}
                                                                    onCancelError={handleCancelError}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {!pullRun.success && pullRun.errorMessage && (
                                                        <tr>
                                                            <td colSpan={4} style={{ padding: '8px 16px', backgroundColor: '#fff5f5', borderTop: 'none', textAlign: 'center' }}>
                                                                <div style={{ fontSize: '0.9rem', color: '#d32f2f', fontWeight: '500' }}>
                                                                    Error Message: {pullRun.errorMessage}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalCount > pageSize && renderPagination()}
                        </div>
                    </div>

                    <div className="modal-footer-custom d-flex justify-content">
                        <div className="d-flex gap-2">
                            <label className="form-label-custom mb-0">
                                Показывать:
                            </label>
                            <select
                                className="form-select-custom mb-0"
                                style={{ minWidth: '80px' }}
                                value={pageSize}
                                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                            <label className="form-label-custom mb-0">
                                Обновлять:
                            </label>
                            <select
                                className="form-select-custom"
                                style={{ minWidth: '160px' }}
                                value={refreshInterval}
                                onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                            >
                                <option value={0}>не нужно</option>
                                <option value={3}>каждые 3 сек</option>
                                <option value={5}>каждые 5 сек</option>
                                <option value={10}>каждые 10 сек</option>
                                <option value={30}>каждые 30 сек</option>
                            </select>
                        </div>
                        <button
                            type="button"
                            className="btn-primary-custom"
                            onClick={onClose}
                        >
                            <span className="btn-icon">✕</span>
                            Закрыть
                        </button>
                    </div>
                </div>
            </div>

            <LogModal
                isOpen={logModal.isOpen}
                onClose={closeLogModal}
                title={logModal.title}
                content={logModal.content}
                isRunning={logModal.isRunning}
                refreshInterval={refreshInterval}
                onRefreshLog={refreshLog}
            />
        </div>
    );
};

export default IntegrationInfoModal;
